import { cn } from "@/lib/utils";
import { getThemeStyle, useThemeCache } from "@/hooks/use-color-theme";

export interface Kpi {
  label: string;
  value: string;
  trend?: string;
  variant?: "default" | "primary" | "danger";
}

export function KpiRow({ items }: { items: Kpi[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 px-4 py-4 sm:px-[22px] md:grid-cols-3 lg:grid-cols-5">
      {items.map((k, i) => (
        <KpiCard key={i} {...k} />
      ))}
    </div>
  );
}

function KpiCard({ label, value, trend, variant = "default" }: Kpi) {
  useThemeCache();
  const isPrimary = variant === "primary";
  const isDanger = variant === "danger";
  const lead = getThemeStyle("card_lead");
  const primaryStyle =
    isPrimary && lead
      ? { backgroundColor: lead.bg_color, color: lead.text_color, borderColor: lead.bg_color }
      : undefined;
  return (
    <div
      style={primaryStyle}
      className={cn(
        "rounded-lg border p-3 shadow-sm",
        isPrimary && !primaryStyle && "border-primary bg-primary text-primary-foreground",
        isDanger && "border-[color:var(--danger)] bg-[color:var(--danger-bg)]",
        !isPrimary && !isDanger && "border-border bg-card",
      )}
    >
      <div
        className={cn(
          "text-[9px] font-semibold uppercase tracking-[0.4px]",
          isPrimary
            ? "text-primary-foreground/70"
            : isDanger
              ? "text-[color:var(--danger-text)]/80"
              : "text-muted-foreground",
        )}
      >
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-[22px] font-extrabold leading-tight",
          isDanger && "text-[color:var(--danger-text)]",
        )}
      >
        {value}
      </div>
      {trend && (
        <div
          className={cn(
            "mt-1 text-[10px] font-medium",
            isPrimary
              ? "text-[color:var(--success)]"
              : isDanger
                ? "text-[color:var(--danger-text)]"
                : "text-[color:var(--success-text)]",
          )}
        >
          {trend}
        </div>
      )}
    </div>
  );
}
